import { observer } from "mobx-react"
import Image from "next/image"
import classNames from "classnames"
import { RouterLink } from "@lib/routing"
import { useStore } from "@features/common"
import { ARTICLE_PREVIEW_CLIPPATH_ID } from "@features/journal"
import BorderSvg from "@assets/media/journal/article-preview-border.svg"
import Border2Svg from "@assets/media/journal/article-preview-border-2.svg"
import StarSvg from "@assets/media/journal/article-preview-star.svg"
import { useCallback, useEffect, useState } from "react"
import * as Sentry from "@sentry/nextjs"

import type { PostModel } from "@serializers/article"
import type { ImageProps } from "next/image"

type ArticlePreviewProps = {
  article: PostModel
  urlPrefix?: string
  classes?: {
    container?: string
  }
  imageProps?: Partial<ImageProps>
  orientation?: "vertical" | "horizontal"
}

export const ArticlePreview = observer(function ArticlePreview(
  props: ArticlePreviewProps,
) {
  const {
    article,
    urlPrefix = "journal",
    classes,
    imageProps,
    orientation = "vertical",
  } = props
  const { ResponsiveModel } = useStore()
  const { image_cover, image_preview } = article
  const isHorizontal =
    orientation === "horizontal" &&
    (ResponsiveModel.tablet || ResponsiveModel.desktop)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  const getPreviewImage = useCallback(() => {
    const imageHorizontalFallback =
      image_cover.formats?.article_cover_original || image_cover
    const imageFallback =
      image_preview.formats?.article_preview_original || image_preview

    if (!ResponsiveModel.isWebPSupported) {
      return (isHorizontal ? imageHorizontalFallback : imageFallback).url
    }

    if (isHorizontal) {
      const image1x = image_cover.formats?.article_cover_desktop || image_cover
      const image2x =
        image_cover.formats?.article_cover_desktop_x2 || image_cover
      const image = ResponsiveModel.isHighResolution ? image2x : image1x
      return image.url
    } else {
      const image1x = image_preview.formats?.article_preview || image_preview
      const image2x = image_preview.formats?.article_preview_x2 || image_preview
      const image = ResponsiveModel.isHighResolution ? image2x : image1x
      return image.url
    }
  }, [
    ResponsiveModel.isWebPSupported,
    ResponsiveModel.isHighResolution,
    isHorizontal,
    image_cover,
    image_preview,
  ])

  const imageSrc = getPreviewImage()
  const imageAlt =
    (isHorizontal ? image_cover : image_preview).alternativeText ||
    article.title
  const imageTitle = (isHorizontal ? image_cover : image_preview).caption

  useEffect(() => {
    if (!ResponsiveModel.isWebPSupported && imageSrc.endsWith(".webp")) {
      Sentry.captureMessage(
        `Article #${article.id} (${article.slug}, ${article.lang}) preview: WebP image isn't supported`,
      )
    }
  }, [
    ResponsiveModel.isWebPSupported,
    imageSrc,
    article.id,
    article.lang,
    article.slug,
  ])

  return (
    <article
      className={classNames(
        "relative bg-gray-100 w-full h-[382px] tablet-s:h-[376px] desktop:h-[709px] will-change-transform overflow-hidden",
        isHorizontal && "h-[441px]",
        classes?.container,
      )}
      style={{
        clipPath: `url(#${ARTICLE_PREVIEW_CLIPPATH_ID})`,
      }}
    >
      <RouterLink
        uri={article.href || `/${urlPrefix}/${article.slug}`}
        routing={!article.href}
        availableLangs={[article.lang]}
        className={classNames(
          "group flex flex-col h-full",
          isHorizontal && "tablet-s:flex-row-reverse",
        )}
        data-test-id="article_preview__card"
      >
        <div className="h-full select-none flex-grow relative transition-transform will-change-transform laptop:group-hover:scale-110 flex shrink">
          <div
            className={classNames(
              "h-full bg-gradient-to-b from-transparent via-gray-50 to-transparent -translate-y-full",
              !ResponsiveModel.is_ios &&
                !ResponsiveModel.is_safari &&
                !isImageLoaded &&
                "animate-[shimmer_3s_infinite]",
            )}
          />
          {imageSrc && (
            <Image
              src={imageSrc}
              title={imageTitle}
              onLoadingComplete={() => setIsImageLoaded(true)}
              layout="fill"
              objectFit="cover"
              alt={imageAlt}
              unoptimized={true}
              draggable={false}
              {...imageProps}
            />
          )}
        </div>
        <div
          className={classNames(
            "relative bg-gray-400 border-t border-gray-400 p-8 pt-6 desktop:p-12 desktop:pt-8",
            isHorizontal &&
              "tablet-s:flex tablet-s:flex-col tablet-s:border-r tablet-s:border-gray-400 desktop:pl-16 desktop:pr-4",
            isHorizontal && article.category
              ? "tablet-s:pt-30 desktop:pt-34"
              : "tablet-s:justify-center",
          )}
        >
          <div
            className={classNames(
              "text-white font-serif text-lg leading-[30px] tablet-s:text-base",
              isHorizontal
                ? "tablet-s:text-xl desktop:text-3xl desktop:leading-9 tablet-s:max-w-[246px]"
                : "desktop:text-2xl",
            )}
          >
            {article.title}
          </div>
          <BorderSvg
            className={classNames(
              "absolute transform-gpu w-full h-11 -top-11 left-0 desktop:h-16 desktop:-top-[63px]",
              isHorizontal && "tablet-s:hidden",
            )}
          />
          {isHorizontal && (
            <Border2Svg className="hidden tablet-s:block absolute top-0 -right-19 w-19 h-full" />
          )}
          <div
            className={classNames(
              "absolute transform-gpu flex items-end space-x-2 -top-4 left-6 desktop:left-12 desktop:space-x-3 desktop:-top-8",
              isHorizontal && "tablet-s:static tablet-s:mt-6",
            )}
          >
            <StarSvg className="w-auto h-6 desktop:h-8 " />
            <StarSvg className="w-auto h-4.5 desktop:h-6" />
          </div>
        </div>
      </RouterLink>
      {article.category ? (
        <RouterLink
          uri={`/journal/categories/${article.category.slug}`}
          className={classNames(
            "absolute left-6 top-6 desktop:top-12 desktop:left-12 transform-gpu",
            "inline-block px-4 pt-2.5 pb-2 bg-white border-black border desktop:pt-4 desktop:pb-3",
            "text-[11px] leading-[18px] uppercase font-sans-bold desktop:text-sm",
            "transition-colors duration-150 ease-in hover:bg-gold-200",
            isHorizontal &&
              "tablet-s:left-8 tablet-s:top-10 desktop:left-16 desktop:top-[62px]",
          )}
          data-test-id="article_preview__category"
        >
          {article.category.name}
        </RouterLink>
      ) : null}
    </article>
  )
})
